import { handleSubmit } from "app/client/lib/formUtils";
import { sanitizeLinkUrl } from "app/client/lib/sanitizeUrl";
import * as css from "app/client/ui/LoginPagesCss";
import { OAuthAPI } from "app/client/ui/OAuthAPI";
import { cssInfoTooltipButton } from "app/client/ui/tooltips";
import { createUserImage } from "app/client/ui/UserImage";
import {
  cssMemberListItem,
  cssMemberPrimary,
  cssMemberSecondary,
  cssMemberText,
} from "app/client/ui/UserItem";
import { icon } from "app/client/ui2018/icons";
import { cssLink } from "app/client/ui2018/links";
import { confirmModal } from "app/client/ui2018/modals";
import { unstyledButton } from "app/client/ui2018/unstyled";
import { commonUrls } from "app/common/gristUrls";
import {
  OAuthClient,
  OAuthConsentPrompt,
  OAuthSelectAccountPrompt,
} from "app/common/LoginPageConfig";
import { tokens } from "app/common/ThemePrefs";
import { Disposable, dom, makeTestId, Observable, styled } from "grainjs";

const testId = makeTestId("test-oauth-");

/**
 * {@link SelectAccountForm} options.
 */
export interface SelectAccountFormOptions {
  api: OAuthAPI;
  client: OAuthClient;
  prompt: OAuthSelectAccountPrompt;
}

/**
 * The form for the OAuth select account page (`/select-account`).
 */
export class SelectAccountForm extends Disposable {
  private _api = this._options.api;

  constructor(private _options: SelectAccountFormOptions) {
    super();
  }

  public buildDom() {
    const { client } = this._options;

    return [
      buildOAuthFormTitle({ client, variant: "select-account" }),
      cssSelectAccountForms(
        this._buildSelectSessionUserForm(),
        this._buildLoginForm(),
      )
    ];
  }

  /**
   * Builds a form to select an existing user from the Grist session.
   */
  private _buildSelectSessionUserForm() {
    const { prompt: { details: { users } } } = this._options;

    return cssSelectAccountForm(
      { action: this._api.getConsentUrl(), method: "GET" },
      cssAccountButtons(
        users.map((user, index) =>
          cssAccountButton(
            cssAccount(
              createUserImage(user, "large"),
              cssAccountNameAndEmail(
                cssMemberPrimary(user.name || dom("span", user.email), testId("select-account-user-name")),
                user.name ? cssMemberSecondary(user.email, testId("select-account-user-email")) : null
              )
            ),
            {
              type: "submit",
              name: "authuser",
              // The array index is a stable identifier for a user in the Grist session.
              //
              // If Grist sessions are ever changed in the future such that they no longer guarantee
              // that indexes remain stable (e.g. a per-user sign-out feature is implemented that
              // causes indexes for existing users in the session to change), a different identifier
              // may need to be used here to prevent a class of bugs related to unstable identifiers.
              value: String(index),
            },
            testId("select-account-user")
          )
        )
      )
    );
  }

  /**
   * Builds a form that redirects to the login page (`/login`) to sign in to a new account.
   */
  private _buildLoginForm() {
    return cssSelectAccountForm(
      cssAccountButtons(
        cssAccountButton(
          cssAccountButton.cls("-add"),
          cssAccount(
            createUserImage("addUser", "large"),
            cssAccountNameAndEmail(cssMemberPrimary("Add account"))
          ),
          { type: "submit" },
          testId("select-account-add-user")
        )
      ),
      handleSubmit({
        onSubmit: () => this._api.redirectToLogin(),
      })
    );
  }
}

/**
 * {@link buildConsentForm} options.
 */
export interface ConsentFormOptions {
  api: OAuthAPI;
  client: OAuthClient;
  prompt: OAuthConsentPrompt;
}

/**
 * The form for the OAuth consent page (`/consent`).
 */
export class ConsentForm extends Disposable {
  private _api = this._options.api;

  constructor(private _options: ConsentFormOptions) {
    super();
  }

  public buildDom() {
    const { client } = this._options;

    return [
      buildOAuthFormTitle({ client, variant: "consent" }),
      this._buildChangeAccountForm(),
      this._buildConsentForm(),
    ];
  }

  /**
   * Builds a form that redirects to the select account page (`/oauth/interaction/:cid/select-account`)
   * to sign in as a different user.
   */
  private _buildChangeAccountForm() {
    const { prompt: { details: { user } } } = this._options;

    // Use a form for returning to the select account page for consistency
    // with the other form on this page, and to disallow opening the select
    // account page in a new tab.
    return cssChangeAccountForm(
      css.flexJustifyCenter(
        cssChangeAccountButton(
          cssAccountSmall(
            createUserImage(user, "small"),
            cssAccountNameAndEmail(cssMemberPrimary(
              dom("span", user.email, testId("consent-change-account-email")))
            ),
            icon("Collapse")
          ),
          testId("consent-change-account")
        )
      ),
      handleSubmit({
        onSubmit: () => this._api.redirectToSelectAccount(),
      })
    );
  }

  /**
   * Builds a form that allows or denies requested scopes and redirects to the OAuth client
   * with an authorization code (if allowed) or an error (if denied).
   */
  private _buildConsentForm() {
    const { client, prompt: { details: { user } } } = this._options;
    const pending = Observable.create(null, false);

    return cssConsentForm(
      dom.autoDispose(pending),
      cssConsentFormSubheading(
        cssBold(client.name),
        " wants to access this info about you",
        testId("consent-subheading")
      ),
      cssSharedInfo(
        cssSharedInfoRow(
          cssSharedInfoIcon("Public"),
          cssSharedInfoValueAndLabel(
            cssSharedInfoValue(user.name, testId("consent-name")),
            cssSharedInfoLabel("Name")
          )
        ),
        cssSharedInfoRow(
          cssSharedInfoIcon("Mail"),
          cssSharedInfoValueAndLabel(
            cssSharedInfoValue(user.email, testId("consent-email")),
            cssSharedInfoLabel("Email address")
          )
        )
      ),
      cssConsentFormButtons(
        css.bigBasicButton("Cancel",
          dom.prop("disabled", pending),
          cssConsentFormButton.cls(""),
          {
            type: "submit",
            name: "decision",
            value: "deny",
          },
          testId("consent-cancel")
        ),
        css.bigPrimaryButton("Authorize",
          dom.prop("disabled", pending),
          cssConsentFormButton.cls(""),
          {
            type: "submit",
            name: "decision",
            value: "allow",
          },
          testId("consent-authorize")
        )
      ),
      cssRedirectHintText(`You'll be redirected to ${client.uri} after authorizing`),
      cssConsentFormLearnMore(
        cssLink("Learn more about Sign in with Grist", {
          href: commonUrls.help,
          target: "_blank",
        })
      ),
      handleSubmit({
        pending,
        onSubmit: (_fields, _form, event) =>
          this._api.consent((event.submitter as HTMLButtonElement).value as "allow" | "deny"),
        onSuccess: ({ redirectUrl }) => window.location.assign(redirectUrl),
      })
    );
  }
}

/**
 * {@link buildOAuthFormTitle} options.
 */
export interface OAuthFormTitleOptions {
  /**
   * OAuth client information.
   */
  client: OAuthClient;
  /**
   * The title variant.
   *
   * May be one of:
   *  - "select-account": Suitable for the select account page (`/select_account`).
   *  - "login": Suitable for the login page (`/login`) to show client branding.
   *  - "consent": Suitable for the consent page (`/consent`).
   */
  variant: "select-account" | "login" | "consent";
}

/**
 * Builds the title section for an OAuth form (`/select-account`, `/consent`)
 * or the login page (`/login`).
 */
export function buildOAuthFormTitle(options: OAuthFormTitleOptions) {
  const { client, variant } = options;

  let heading: string;
  switch (variant) {
    case "select-account": {
      heading = "Select a Grist account";
      break;
    }
    case "consent": {
      heading = `Sign in to ${client.name}`;
      break;
    }
    case "login": {
      heading = `Sign in to Grist`;
      break;
    }
    default: {
      const exhaustiveCheck: never = variant;
      throw new Error(`Unhandled OAuth form title variant: ${exhaustiveCheck}`);
    }
  }

  return cssOAuthFormTitle(
    css.flexJustifyCenter(css.gristLogo()),
    cssOAuthFormHeading(heading, testId("heading")),
    variant !== "consent"
      ? cssOAuthFormSubheading("to continue to ", cssBold(client.name), testId("subheading"))
      : null,
    cssOAuthFormClientUri(
      css.lightText(client.uri, testId("client-uri")),
      cssInfoTooltipButton("?",
        dom.on("click", () =>
          confirmModal("Grist installation info", "Got it", () => {}, {
            explanation: dom("div",
              dom("p", cssBold("Name: "), dom("span", client.name, testId("client-info-name"))),
              dom("p",
                cssBold("URL: "),
                cssLinkBreakAll(client.uri, {
                  href: sanitizeLinkUrl(client.uri),
                  target: "_blank",
                }, testId("client-info-uri"))
              ),
              dom("p",
                cssBold("Contact: "),
                cssLinkBreakAll(client.contact, {
                  href: sanitizeLinkUrl(`mailto:${client.contact}`),
                }, testId("client-info-contact"))
              )
            ),
            hideCancel: true,
          })
        ),
        testId("client-info")
      )
    )
  );
}

const cssSelectAccountForms = styled("div", `
  align-items: center;
  display: flex;
  flex-direction: column;
  height: 100%;
  width: 100%;
`);

const cssSelectAccountForm = styled("form", `
  max-width: 400px;
  width: 100%;
`);

const cssAccountButtons = styled("div", `
  display: flex;
  flex-direction: column;
  position: relative;
`);

const cssAccountButton = styled(unstyledButton, `
  border-radius: 16px;
  cursor: pointer;
  transition: background-color 0.2s linear;

  &:focus {
    outline: none;
  }

  &:hover, &:focus {
    background-color: ${tokens.bgTertiary};
  }

  &:not(&-add)::after {
    border-bottom: 1px solid ${tokens.decoration};
    content: "";
    height: 0;
    left: 16px;
    position: absolute;
    width: calc(100% - 32px);
  }
`);

const cssAccount = styled(cssMemberListItem, `
  align-items: center;
  gap: 16px;
  margin: 0;
  min-height: unset;
  padding: 16px;
  width: 100%;
`);

const cssAccountSmall = styled(cssAccount, `
  padding: 4px;
`);

const cssAccountNameAndEmail = styled(cssMemberText, `
  align-items: flex-start;
  margin: 0;
`);

const cssChangeAccountForm = styled("form", `
  margin-bottom: 24px;
`);

const cssChangeAccountButton = styled(unstyledButton, `
  border: 1px solid ${tokens.secondary};
  border-radius: 16px;
  cursor: pointer;

  &:hover, &:focus {
    background-color: ${tokens.bgTertiary};
  }
`);

const cssConsentForm = styled("form", `
  width: 100%;
`);

const cssConsentFormSubheading = styled("div", `
  font-size: 16px;
  margin-bottom: 24px;
`);

const cssBold = styled('span', `
  font-weight: 600;
`);

const cssSharedInfo = styled("div", `
  display: flex;
  flex-direction: column;
  gap: 8px;
  margin-bottom: 48px;
  padding: 0 16px;
`);

const cssSharedInfoRow = styled("div", `
  display: flex;
  gap: 16px;
`);

const cssSharedInfoIcon = styled(icon, `
  width: 15px;
  height: 15px;
  flex-shrink: 0;
  --icon-color: ${tokens.black};
`);

const cssSharedInfoValueAndLabel = styled("div", `
  display: flex;
  flex-direction: column;
  flex-grow: 1;
  gap: 4px;
`);

const cssSharedInfoValue = styled("span", `
  color: ${tokens.black};
  font-size: 15px;
  line-height: 15px;
`);

const cssSharedInfoLabel = styled("span", `
  color: ${tokens.secondary};
  font-size: 13px;
`);

const cssConsentFormButtons = styled("div", `
  display: flex;
  gap: 16px;
  margin-bottom: 16px;
  margin-top: 32px;
`);

const cssConsentFormButton = styled("button", `
  width: 50%;
`);

const cssConsentFormLearnMore = styled(css.flexJustifyCenter, `
  margin-top: 24px;
`);

const cssRedirectHintText = styled(css.lightText, `
  font-size: 13px;
  text-align: center;
`);

const cssOAuthFormTitle = styled("div", `
  margin-bottom: 24px;
`);

const cssOAuthFormHeading = styled(css.formHeading, `
  align-items: center;
  display: flex;
  gap: 16px;
  justify-content: center;
  margin-bottom: 0px;
  margin-top: 16px;
`);

const cssOAuthFormSubheading = styled(css.text, `
  text-align: center;
  line-height: 22px;
  font-size: 15px;
`);

const cssOAuthFormClientUri = styled(css.flexJustifyCenter, `
  align-items: center;
  gap: 4px;
  overflow: hidden;
  text-overflow: ellipsis;
`);

const cssLinkBreakAll = styled(cssLink, `
  word-break: break-all;
`);
